import { wrapLine, pdfEscapeLiteral, encodeLatin1, concatBytes } from "./stringUtils";

export function buildSimplePdfFromLines(lines) {
  // Minimal PDF generator (Type1 Helvetica). Good enough for invoices/statements.
  const PAGE_BREAK = "__PAGE_BREAK__";
  const wrapped = [];
  for (const l of Array.isArray(lines) ? lines : []) {
    if (l === PAGE_BREAK) {
      wrapped.push(PAGE_BREAK);
      continue;
    }
    wrapped.push(...wrapLine(l, 95));
  }

  const pageWidth = 595;
  const pageTopY = 800;
  const marginX = 50;
  const lineH = 14;
  const maxLinesPerPage = Math.floor((pageTopY - 60) / lineH);
  const pages = [];
  let cur = [];
  const pushCur = () => {
    pages.push(cur.length ? cur : [""]);
    cur = [];
  };
  for (const l of wrapped) {
    if (l === PAGE_BREAK) {
      pushCur();
      continue;
    }
    cur.push(l);
    if (cur.length >= maxLinesPerPage) pushCur();
  }
  if (cur.length || pages.length === 0) pushCur(); 

  const header = "%PDF-1.3\n";

  // Build objects as strings first, then compute offsets.
  // 1: catalog, 2: pages, 3..: page objects, font, contents per page.
  const numPages = pages.length;

  const catalogObjNum = 1;
  const pagesObjNum = 2;
  const fontObjNum = 3;

  // Page object numbers start at 4
  const firstPageObjNum = 4;
  const firstContentObjNum = firstPageObjNum + numPages;

  const pageKids = [];
  for (let i = 0; i < numPages; i++) {
    pageKids.push(`${firstPageObjNum + i} 0 R`);
  }

  const catalog = `${catalogObjNum} 0 obj\n<< /Type /Catalog /Pages ${pagesObjNum} 0 R >>\nendobj\n`;
  const pagesObj = `${pagesObjNum} 0 obj\n<< /Type /Pages /Kids [${pageKids.join(" ")}] /Count ${numPages} >>\nendobj\n`;
  const fontObj = `${fontObjNum} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`;

  const pageObjs = [];
  const contentObjs = [];

  for (let p = 0; p < numPages; p++) {
    const pageNum = firstPageObjNum + p;
    const contentNum = firstContentObjNum + p;

    // Build content stream
    const linesOnPage = pages[p] || [];
    let content = "BT\n/F1 11 Tf\n";
    content += `${marginX} ${pageTopY} Td\n`;
    for (let i = 0; i < linesOnPage.length; i++) {
      const lit = pdfEscapeLiteral(linesOnPage[i]);
      content += `(${lit}) Tj\n`;
      if (i !== linesOnPage.length - 1) content += `0 -${lineH} Td\n`;
    }
    content += "\nET\n";

    // Footer: page numbers
    const footerY = 35;
    const footerX = pageWidth - marginX - 90;
    content += `BT\n/F1 9 Tf\n${footerX} ${footerY} Td\n(Page ${p + 1} of ${numPages}) Tj\nET\n`;

    const contentBytes = encodeLatin1(content);
    const contentObj = `${contentNum} 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n${content}\nendstream\nendobj\n`;
    contentObjs.push(contentObj);

    const pageObj = `${pageNum} 0 obj\n<< /Type /Page /Parent ${pagesObjNum} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontObjNum} 0 R >> >> /Contents ${contentNum} 0 R >>\nendobj\n`;
    pageObjs.push(pageObj);
  }

  // Build PDF with xref offsets.
  // xref expects entry per object number (0..maxObj)
  // Our objects list includes header + object definitions. Header is not an object, so we compute offsets based on actual objects.
  // We'll rebuild the byte stream while tracking offsets for each object number.
  const rebuiltParts = [];
  let pos = 0;
  // Write header first
  const headerBytes = encodeLatin1(header);
  rebuiltParts.push(headerBytes);
  pos += headerBytes.length;

  const maxObj = firstContentObjNum + numPages - 1;
  const xrefOffsets = new Array(maxObj + 1).fill(0);
  xrefOffsets[0] = 0;

  const objStrings = [catalog, pagesObj, fontObj, ...pageObjs, ...contentObjs];
  const objNums = [];
  // catalog/pages/font fixed
  objNums.push(catalogObjNum, pagesObjNum, fontObjNum);
  for (let i = 0; i < numPages; i++) objNums.push(firstPageObjNum + i);
  for (let i = 0; i < numPages; i++) objNums.push(firstContentObjNum + i);

  for (let i = 0; i < objStrings.length; i++) {
    const s = objStrings[i];
    const num = objNums[i];
    xrefOffsets[num] = pos;
    const b = encodeLatin1(s);
    rebuiltParts.push(b);
    pos += b.length;
  }

  const xrefStart = pos;
  let xref = "xref\n";
  xref += `0 ${maxObj + 1}\n`;
  xref += "0000000000 65535 f \n";
  for (let n = 1; n <= maxObj; n++) {
    const offStr = String(xrefOffsets[n] || 0).padStart(10, "0");
    xref += `${offStr} 00000 n \n`;
  }
  const trailer = `trailer\n<< /Size ${maxObj + 1} /Root ${catalogObjNum} 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;

  rebuiltParts.push(encodeLatin1(xref));
  rebuiltParts.push(encodeLatin1(trailer));
  
  return concatBytes(rebuiltParts);
}

export async function loadImageRgbForPdf(url, maxSize = 90) {
  const u = String(url || "").trim();
  if (!u) return null;
  return await new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const iw = img.naturalWidth || img.width || 0;
          const ih = img.naturalHeight || img.height || 0;
          if (!iw || !ih) return resolve(null);
          const scale = Math.min(1, maxSize / Math.max(iw, ih));
          const w = Math.max(1, Math.round(iw * scale));
          const h = Math.max(1, Math.round(ih * scale));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) return resolve(null);
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          const id = ctx.getImageData(0, 0, w, h);
          const data = id.data || new Uint8ClampedArray(0);
          const rgb = new Uint8Array(w * h * 3);
          let j = 0;
          for (let i = 0; i < data.length; i += 4) {
            rgb[j++] = data[i];
            rgb[j++] = data[i + 1];
            rgb[j++] = data[i + 2];
          }
          resolve({ width: w, height: h, rgb });
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = u;
    } catch {
      resolve(null);
    }
  });
}

export function buildPdfFromLinesWithHeader(lines, opts = {}) {
  const headerRightLines = Array.isArray(opts.headerRightLines) ? opts.headerRightLines : [];
  const logo = opts && opts.logo && opts.logo.rgb ? opts.logo : null;

  // Wrap body lines (supports explicit page breaks via the special marker)
  const PAGE_BREAK = "__PAGE_BREAK__";
  const wrapped = [];
  for (const l of Array.isArray(lines) ? lines : []) {
    if (l === PAGE_BREAK) {
      wrapped.push(PAGE_BREAK);
      continue;
    }
    wrapped.push(...wrapLine(l, 95));
  }

  const includeHeader = !!logo || headerRightLines.length > 0;
  const pageWidth = 595;
  const pageHeight = 842;
  const marginX = 50;
  const lineH = 14;
  // Layout tweaks requested:
  // - move right header block 2 lines lower
  // - move the main body text ("Monthly consolidated statement...") 3 lines lower
  const bodyTopY = includeHeader ? 690 - (3 * lineH) : 800;

  const maxLinesPerPage = Math.floor((bodyTopY - 60) / lineH);

  // Build pages with support for explicit page breaks and automatic overflow paging
  const pages = [];
  let cur = [];
  const pushCur = () => {
    pages.push(cur.length ? cur : [""]);
    cur = [];
  };

  for (const l of wrapped) {
    if (l === PAGE_BREAK) {
      pushCur();
      continue;
    }
    cur.push(l);
    if (cur.length >= maxLinesPerPage) {
      pushCur();
    }
  }
  if (cur.length || pages.length === 0) pushCur();

  const header = "%PDF-1.3\n";
  const numPages = pages.length;

  const catalogObjNum = 1;
  const pagesObjNum = 2;
  const fontObjNum = 3;
  const imageObjNum = logo ? 4 : null;

  const firstPageObjNum = logo ? 5 : 4;
  const firstContentObjNum = firstPageObjNum + numPages;

  const pageKids = [];
  for (let i = 0; i < numPages; i++) pageKids.push(`${firstPageObjNum + i} 0 R`);

  const catalog = `${catalogObjNum} 0 obj\n<< /Type /Catalog /Pages ${pagesObjNum} 0 R >>\nendobj\n`;
  const pagesObj = `${pagesObjNum} 0 obj\n<< /Type /Pages /Kids [${pageKids.join(" ")}] /Count ${numPages} >>\nendobj\n`;
  const fontObj = `${fontObjNum} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`;

  const objects = [];

  const addObjStr = (num, s) => objects.push({ num, bytes: encodeLatin1(s) });
  const addObjBytes = (num, bytes) => objects.push({ num, bytes });

  addObjStr(catalogObjNum, catalog);
  addObjStr(pagesObjNum, pagesObj);
  addObjStr(fontObjNum, fontObj);

  if (logo && imageObjNum) {
    const imgStreamHeader = `${imageObjNum} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${logo.width} /Height ${logo.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Length ${logo.rgb.length} >>\nstream\n`;
    const imgStreamFooter = "\nendstream\nendobj\n";
    addObjBytes(imageObjNum, concatBytes([encodeLatin1(imgStreamHeader), logo.rgb, encodeLatin1(imgStreamFooter)]));
  }

  for (let p = 0; p < numPages; p++) {
    const pageNum = firstPageObjNum + p;
    const contentNum = firstContentObjNum + p;

    const linesOnPage = pages[p] || [];
    let content = "";

    if (includeHeader && p === 0) {
      if (logo) {
        const imgX = marginX;
        const imgY = pageHeight - 60 - logo.height;
        content += `q\n${logo.width} 0 0 ${logo.height} ${imgX} ${imgY} cm\n/Im1 Do\nQ\n`;
      }
      if (headerRightLines.length > 0) {
        const rightX = 330;
        // Move the right header block 2 lines lower (each line step is 12).
        const rightY = pageHeight - 60 - (2 * 12);
        content += "BT\n/F1 10 Tf\n";
        content += `${rightX} ${rightY} Td\n`;
        for (let i = 0; i < headerRightLines.length; i++) {
          const lit = pdfEscapeLiteral(headerRightLines[i]);
          content += `(${lit}) Tj\n`;
          if (i !== headerRightLines.length - 1) content += "0 -12 Td\n";
        }
        content += "\nET\n";
      }
    }

    content += "BT\n/F1 11 Tf\n";
    content += `${marginX} ${bodyTopY} Td\n`;
    for (let i = 0; i < linesOnPage.length; i++) {
      const lit = pdfEscapeLiteral(linesOnPage[i]);
      content += `(${lit}) Tj\n`;
      if (i !== linesOnPage.length - 1) content += `0 -${lineH} Td\n`;
    }
    content += "\nET\n";

    // Footer: page numbers
    const footerY = 35;
    const footerX = pageWidth - marginX - 90;
    content += `BT\n/F1 9 Tf\n${footerX} ${footerY} Td\n(Page ${p + 1} of ${numPages}) Tj\nET\n`;

    const contentBytes = encodeLatin1(content);
    const contentObj = `${contentNum} 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n${content}\nendstream\nendobj\n`;
    addObjStr(contentNum, contentObj);

    const xobjPart = logo ? ` /XObject << /Im1 ${imageObjNum} 0 R >>` : "";
    const pageObj = `${pageNum} 0 obj\n<< /Type /Page /Parent ${pagesObjNum} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontObjNum} 0 R >>${xobjPart} >> /Contents ${contentNum} 0 R >>\nendobj\n`;
    addObjStr(pageNum, pageObj);
  }

  objects.sort((a, b) => a.num - b.num);

  const headerBytes = encodeLatin1(header);
  let pos = headerBytes.length;

  const maxObj = firstContentObjNum + numPages - 1;
  const xrefOffsets = new Array(maxObj + 1).fill(0);
  xrefOffsets[0] = 0;

  const outParts = [headerBytes];
  for (const o of objects) {
    xrefOffsets[o.num] = pos;
    outParts.push(o.bytes);
    pos += o.bytes.length;
  }

  const xrefStart = pos;
  let xref = "xref\n";
  xref += `0 ${maxObj + 1}\n`;
  xref += "0000000000 65535 f \n";
  for (let n = 1; n <= maxObj; n++) {
    const offStr = String(xrefOffsets[n] || 0).padStart(10, "0");
    xref += `${offStr} 00000 n \n`;
  }
  const trailer = `trailer\n<< /Size ${maxObj + 1} /Root ${catalogObjNum} 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;

  outParts.push(encodeLatin1(xref));
  outParts.push(encodeLatin1(trailer));
  return concatBytes(outParts);
}
